import React from "react";
import {
    ArrowRightIcon,
    LightbulbIcon,
    CalendarPlusIcon,
    WaypointsIcon,
    ChartNoAxesGanttIcon,
    RocketIcon,
    PackageCheckIcon,
    ListCheckIcon,
    FileTextIcon,
    PaperclipIcon,
    FilePlusIcon,
    FileCheckIcon,
    FileWarningIcon,
    FilePenIcon,
    CircleCheckIcon,
} from "lucide-react";
import { projectEventSchema } from "tekkr-common/dist/model/project/events";
import { DateTime } from "luxon";
import { TekkrMarkdown } from "../../components/shared/markdown/tekkr-markdown";
import { z } from "zod";
import BackButtonRow from "../../components/shared/back-button-row";
import Spinner from "../../components/ui/spinner";
import { UserAvatarAndName } from "../../components/shared/org/avatar-for-user";
import { useProjectAttachments } from "../../data/project-attachments";
import { useProject } from "../../data/project";
import { ProjectWorkstreamIcon } from "../../components/shared/project/workstream-header";
import { DeliverableIcon } from "../../components/shared/project/deliverable-card";
import { cn, positions } from "../../lib/utils";
import { ProjectPageHeader } from "../project/components/project-page-header";
import { useProjectEventsQuery } from "../../data/queries/project-events";
import { useAttachmentDisplay } from "../../lib/attachments/attachments";
import { HttpAttachment } from "tekkr-common/dist/model/attachments";
import { useSetProjectChatContext } from "../project/components/project-chat";

type ProjectEvent = z.infer<typeof projectEventSchema>;

function formatTaskStatus(status: string | null): string {
    if (status === null) return "Pending";
    if (status === "done") return "Done";
    if (status === "skipped") return "Skipped";
    return status;
}

const eventTypeConfig: Record<ProjectEvent["event"]["type"], { name: string; icon: typeof ArrowRightIcon }> = {
    learning_tracked: { name: "Learning Tracked", icon: LightbulbIcon },
    meeting_scheduled: { name: "Meeting Scheduled", icon: CalendarPlusIcon },
    next_steps_updated: { name: "Next Steps Updated", icon: WaypointsIcon },
    plan_updated: { name: "Plan Updated", icon: ChartNoAxesGanttIcon },
    project_started: { name: "Project Started", icon: RocketIcon },
    deliverable_delivered: { name: "Deliverable Completed", icon: PackageCheckIcon },
    workstream_completed: { name: "Workstream Completed", icon: ListCheckIcon },
    workstream_started: { name: "Workstream Started", icon: ListCheckIcon },
    meeting_completed_summary_available: { name: "Meeting Summary Available", icon: FileTextIcon },
    attachment_created: { name: "Attachment Created", icon: FilePlusIcon },
    document_generated: { name: "Document Generated", icon: FileCheckIcon },
    document_marked_stale: { name: "Document Marked Stale", icon: FileWarningIcon },
    document_update_applied: { name: "Document Updated", icon: FilePenIcon },
    task_status_changed: { name: "Task Status Changed", icon: CircleCheckIcon },
    project_owner_changed: { name: "Project Owner Changed", icon: ArrowRightIcon },
};

function EventItem(props: { event: ProjectEvent }) {
    const typeConfig = eventTypeConfig[props.event.event.type]!;
    const Icon = typeConfig.icon;
    return (
        <div className={"grow"}>
            <div className={"mb-2 mt-2.5 text-sm font-medium text-muted-foreground"}>
                {DateTime.fromISO(props.event.date).toLocaleString({
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                })}
            </div>
            <div className={"mb-2 flex w-full grow flex-col gap-1 rounded-md border px-3 py-2"}>
                <div className={"flex flex-row items-center gap-2 font-medium"}>
                    <Icon className={"h-4 w-4"} />
                    {typeConfig.name}
                    <ActorView actor={props.event.actor} />
                </div>
                <EventItemBody event={props.event.event} />
            </div>
        </div>
    );
}

function ActorView(props: { actor: ProjectEvent["actor"] }) {
    if (!props.actor) {
        return null; // backwards compatibility, if no actor is present, we don't show anything
    }
    if ("userId" in props.actor) {
        return (
            <>
                <div className={"m-1 w-[1px] self-stretch bg-muted"}></div>
                <UserAvatarAndName
                    userId={props.actor.userId}
                    avatarClassName={"w-4 h-4"}
                    nameClassName={"text-muted-foreground text-sm font-medium"}
                />
            </>
        );
    }
    return null;
}

function EventItemBody(props: { event: ProjectEvent["event"] }) {
    if (props.event.type === "learning_tracked") {
        return <LearningTrackedEventItem event={props.event} />;
    } else if (props.event.type === "plan_updated") {
        return <PlanUpdatedEventItem event={props.event} />;
    } else if (props.event.type === "meeting_completed_summary_available") {
        return <MeetingCompletedSummaryAvailableEventItem event={props.event} />;
    } else if (props.event.type === "workstream_started" || props.event.type === "workstream_completed") {
        return <WorkstreamReferenceEventItem event={props.event} />;
    } else if (props.event.type === "deliverable_delivered") {
        return <DeliverableReferenceEventItem event={props.event} />;
    } else if (props.event.type === "attachment_created") {
        return <AttachmentCreatedEventItem event={props.event} />;
    } else if (props.event.type === "task_status_changed") {
        return <TaskStatusChangedEventItem event={props.event} />;
    } else if (props.event.type === "project_owner_changed") {
        return <ProjectOwnerChangedEventItem event={props.event} />;
    }
    return <></>;
}

function ProjectOwnerChangedEventItem(props: {
    event: Extract<ProjectEvent["event"], { type: "project_owner_changed" }>;
}) {
    return (
        <div className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
            Ownership transferred from
            <UserAvatarAndName
                userId={props.event.previousOwnerId}
                avatarClassName="w-4 h-4"
                nameClassName="font-medium text-foreground"
            />
            to
            <UserAvatarAndName
                userId={props.event.newOwnerId}
                avatarClassName="w-4 h-4"
                nameClassName="font-medium text-foreground"
            />
        </div>
    );
}

function AttachmentCreatedEventItem(props: { event: Extract<ProjectEvent["event"], { type: "attachment_created" }> }) {
    const attachments = useProjectAttachments();

    if (attachments.isLoading) {
        return (
            <div className={"flex flex-row items-center gap-2 text-sm text-muted-foreground"}>
                <Spinner className={"h-4 w-4"} /> Loading attachments...
            </div>
        );
    }

    const attachment = attachments.getAttachmentById(props.event.attachmentId);
    if (!attachment) {
        return <div className={"text-sm italic text-muted-foreground"}>Attachment not found</div>;
    }

    return <AttachmentPreview attachment={attachment} />;
}

function AttachmentPreview({ attachment }: { attachment: HttpAttachment }) {
    const { icon: Icon, type, url } = useAttachmentDisplay(attachment);
    const open = () => {
        window.open(url, "_blank");
    };

    return (
        <div
            onClick={open}
            className={
                "mt-2 flex cursor-pointer flex-row items-center gap-2 self-start rounded-md bg-accent px-2 py-1 transition-all hover:bg-secondary"
            }
        >
            <PaperclipIcon className={"h-4 w-4 text-muted-foreground"} />
            {<Icon className={"inline h-4 w-4 text-muted-foreground"} />}
            <span className={"text-sm font-medium text-muted-foreground"}>{type}</span>
        </div>
    );
}

function WorkstreamReferenceEventItem(props: { event: { workstreamId: string } }) {
    const { project } = useProject();
    const wsIndex = project.outline.workstreams.findIndex((w) => w.id === props.event.workstreamId);
    const workstream = project.outline.workstreams[wsIndex];

    return (
        <div
            className={cn(
                "flex flex-row items-center gap-2 self-start rounded-md py-1 text-sm",
                !workstream && "opacity-60"
            )}
        >
            <ProjectWorkstreamIcon
                ws={{ id: props.event.workstreamId }}
                index={wsIndex >= 0 ? wsIndex : undefined}
                size={"xs"}
            />
            <span className={"font-medium text-muted-foreground"}>{workstream?.name ?? "Deleted workstream"}</span>
        </div>
    );
}

function DeliverableReferenceEventItem(props: { event: { deliverableId: string; workstreamId: string } }) {
    const { project } = useProject();
    const wsIndex = project.outline.workstreams.findIndex((w) => w.id === props.event.workstreamId);
    const workstream = project.outline.workstreams[wsIndex];
    const dIndex = workstream?.deliverables.findIndex((d) => d.id === props.event.deliverableId) ?? -1;
    const deliverable = workstream?.deliverables[dIndex];

    return (
        <div className={cn((!workstream || !deliverable) && "opacity-60")}>
            <div className={"flex flex-row items-center gap-2 self-start rounded-md py-1 text-sm"}>
                <DeliverableIcon
                    workstreamIndex={wsIndex >= 0 ? wsIndex : undefined}
                    deliverableIndex={dIndex >= 0 ? dIndex : undefined}
                    size={"xs"}
                />
                <span className={"font-medium text-muted-foreground"}>
                    {deliverable?.name ?? "Deleted deliverable"}
                </span>
            </div>
            <div className={"mt-1 text-xs"}>Workstream: {workstream?.name ?? "Deleted workstream"}</div>
        </div>
    );
}

type LearningTrackedEvent = Extract<ProjectEvent["event"], { type: "learning_tracked" }>;
function LearningTrackedEventItem(props: { event: LearningTrackedEvent }) {
    return (
        <div className={"rounded-md bg-accent p-2 text-sm text-muted-foreground"}>
            <TekkrMarkdown markdown={props.event.learning} />
            <div className={"mt-2 flex flex-row items-center gap-1 text-xs"}>
                <span className={"font-medium"}>Source:</span>
                <LearningTrackedEventSourceView source={props.event.source} />
            </div>
        </div>
    );
}

function LearningTrackedEventSourceView(props: { source: LearningTrackedEvent["source"] }) {
    if (!props.source) {
        return <>Not tracked</>;
    }
    if ("chatWithUser" in props.source) {
        return (
            <>
                Chat with <UserAvatarAndName userId={props.source.chatWithUser} avatarClassName={"w-4 h-4"} />
            </>
        );
    } else if ("meeting" in props.source) {
        return <span className={"text-muted-foreground"}>Meeting Transcript</span>;
    }
    return <>Unknown</>;
}

function MeetingCompletedSummaryAvailableEventItem(props: {
    event: Extract<ProjectEvent["event"], { type: "meeting_completed_summary_available" }>;
}) {
    return (
        <div className={"text-sm"}>
            <span className={"font-medium text-muted-foreground"}>
                Summary for meeting: {props.event.meetingTitle} is available
            </span>
        </div>
    );
}

function PlanUpdatedEventItem(props: { event: Extract<ProjectEvent["event"], { type: "plan_updated" }> }) {
    return (
        <div className={"text-sm"}>
            <span className={"font-medium text-muted-foreground"}>Reason: </span>
            {props.event.reason}
        </div>
    );
}

function TaskStatusChangedEventItem(props: { event: Extract<ProjectEvent["event"], { type: "task_status_changed" }> }) {
    const { project } = useProject();
    const task = project.tasks?.find((t) => t.id === props.event.taskId);
    return (
        <div className={"text-sm"}>
            <div className={"font-medium text-muted-foreground"}>{task?.title ?? "Unknown Task"}</div>
            <div className={"mt-1 text-xs text-muted-foreground"}>
                {formatTaskStatus(props.event.previousStatus)} <ArrowRightIcon className={"inline h-3 w-3"} />{" "}
                {formatTaskStatus(props.event.newStatus)}
            </div>
        </div>
    );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ProjectHistory() {
    const query = useProjectEventsQuery();
    if (!query.data) {
        return <div>loading...</div>;
    }
    return (
        <div className={"flex flex-col"}>
            {query.data.map((h, i) => {
                const { first, last } = positions(query.data, i);
                return (
                    <div key={i} className={"flex w-full flex-row items-center gap-3"}>
                        <div className={"flex shrink-0 flex-col items-center self-stretch"}>
                            <div className={cn("h-5 w-0.5 grow bg-muted-foreground", first && "bg-transparent")}></div>
                            <div className={"h-2 w-2 shrink-0 grow-0 rounded-full bg-muted-foreground"}></div>
                            <div
                                className={cn("h-full w-0.5 grow bg-muted-foreground", last && "bg-transparent")}
                            ></div>
                        </div>
                        <EventItem event={h} />
                    </div>
                );
            })}
        </div>
    );
}

export function ProjectHistoryPage() {
    useSetProjectChatContext({ type: "project-history" });

    return (
        <div className={"flex flex-col items-center py-6"}>
            <div className={"flex w-full max-w-[90%] flex-row items-stretch gap-4 lg:gap-8"}>
                <div className={"flex w-full flex-col gap-2 p-1"}>
                    <div className={"flex flex-col gap-3"}>
                        <BackButtonRow />
                        <div className={"mt-2 flex w-full max-w-3xl flex-col gap-3 self-center"}>
                            <ProjectPageHeader preheader={"Project History"} />
                            <ProjectHistory />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
